<template>
	<header class="bg-white shadow shadow-gray-light py-5">
		<div class="container">
			<form
				action="#"
				@submit.prevent="startSearch()"
				@keydown.f3.prevent="searchPrevNextShortcut"
				@keydown.esc.prevent="setSearchMode(false)"
				v-if="searchMode"
			>
				<div class="flex items-center">
					<!-- search mode -->
					<div class="grow pe-3">
						<SearchInput
							v-model="searchString"
							ref="searchInput"
							placeholder="Hledat..."
							class="shadow-lg shadow-gray-light h-11.5"
						></SearchInput>
					</div>
					<div class="shrink-0 flex items-center gap-3">
						<div class="flex items-center text-sm" v-show="showingResults">
							<!-- Next / Prev arrows -->
							<button
								class="btn btn-primary-link px-2"
								title="Předchozí"
								type="button"
								:disabled="
									chatStore.searchChannelMessagesResultItemsFlat.length <= 1
								"
								@click.prevent="
									goToSearchStep(
										chatStore.searchChannelMessagesCurrentSearchStep - 1
									)
								"
							>
								<ChevronUp class="size-3"></ChevronUp>
							</button>
							<span>
								{{ chatStore.searchChannelMessagesCurrentSearchStep }}/{{
									chatStore.searchChannelMessagesResultItemsFlat.length
								}}
							</span>
							<button
								class="btn btn-primary-link px-2"
								title="Další"
								type="button"
								:disabled="
									chatStore.searchChannelMessagesResultItemsFlat.length <= 1
								"
								@click.prevent="
									goToSearchStep(
										chatStore.searchChannelMessagesCurrentSearchStep + 1
									)
								"
							>
								<ChevronDown class="size-3"></ChevronDown>
							</button>
						</div>
						<div
							:class="[
								'size-11.5 relative transition-all',
								searchString.length ? 'me-0 opacity-100' : '-ms-14.5 opacity-0'
							]"
							v-show="!showingResults"
						>
							<!-- Trigger search button -->
							<button
								class="relative btn btn-primary-light text-center rounded-full px-3"
								title="Vyhledat"
								type="submit"
								:disabled="!canStartSearch || searchIsLoading"
							>
								<ChevronRight class="size-5 p-1"></ChevronRight>

								<!-- Loading -->
								<Transition name="fade">
									<div
										v-if="searchIsLoading"
										class="absolute inset-0 grid place-items-center bg-white/70"
									>
										<Spinner class="size-6 text-primary"></Spinner>
									</div>
								</Transition>
							</button>
						</div>
						<button
							class="btn btn-secondary-light text-center rounded-full px-3"
							title="Zrušit hledání"
							type="button"
							@click.prevent="setSearchMode(false)"
						>
							<!-- close chat -->
							<DismissCircleFill class="size-5"></DismissCircleFill>
						</button>
					</div>
				</div>
			</form>
			<div class="flex items-center" v-else>
				<div class="shrink-0">
					<!-- back -->
					<button
						class="btn btn-secondary-light text-center rounded-full px-3"
						title="Zpět"
						type="button"
						@click.prevent="goBack()"
					>
						<ChevronLeft class="size-5 p-1"></ChevronLeft>
					</button>
				</div>
				<div class="grow px-5 min-w-px">
					<div class="flex items-center min-w-px">
						<aside class="shrink-0 pe-3">
							<ChannelIcon
								:type="
									chatStore.currentChannel?.isArchived ? 'archive' : 'active'
								"
								size="sm"
							></ChannelIcon>
						</aside>
						<div class="grow min-w-px">
							<p class="truncate font-medium">
								{{ chatStore.currentChannel?.channelName }}
							</p>
						</div>
					</div>
				</div>
				<div class="shrink-0">
					<!-- search -->
					<button
						class="btn btn-primary-light text-center rounded-full px-3"
						title="Vyhledat"
						type="button"
						@click.prevent="setSearchMode(true)"
					>
						<SearchMagnifier class="size-5 p-1"></SearchMagnifier>
					</button>
				</div>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import ChannelIcon from './ChannelIcon.vue'
import ChevronLeft from '@/assets/icons/chevron-left.svg'
import ChevronRight from '@/assets/icons/chevron-right.svg'
import ChevronUp from '@/assets/icons/chevron-up.svg'
import ChevronDown from '@/assets/icons/chevron-down.svg'
import SearchMagnifier from '@/assets/icons/search-magnifier.svg'
import Spinner from '@/assets/icons/spinner.svg'
import SearchInput from './SearchInput.vue'
import DismissCircleFill from '@/assets/icons/dismiss-circle-fill.svg'
import { ref, nextTick, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
const router = useRouter()
import { useChatStore } from '@/stores/chatStore'
const chatStore = useChatStore()

/**
 * search
 */
const searchString = ref<string>('')
const searchMode = ref<boolean>(false) // search vs. normal header toggle
const searchInput = ref<InstanceType<typeof SearchInput> | null>(null) // ref to input

/**
 * toggles search mode
 * if searchbar appears, focus search input
 * @param state
 */
const setSearchMode = async (state: boolean) => {
	searchMode.value = state
	searchString.value = ''
	clearSearch()

	if (state) {
		await nextTick()
		searchInput.value?.focus()
	}
}

/**
 * we can search only with at least 3 characters
 */
const canStartSearch = computed<boolean>(() => {
	return searchString.value.length >= 3
})

/**
 * stop search when value changes
 */
watch(searchString, (newVal) => {
	clearSearch()
})

/**
 * clears active search settings
 */
const clearSearch = () => {
	showingResults.value = false
	chatStore.searchChannelMessagesResultItems = []
	chatStore.searchChannelMessagesCurrentSearchStep = 0
}

const showingResults = ref<boolean>(false)
const searchIsLoading = ref<boolean>(false)
const startSearch = async () => {
	clearSearch()

	if (!canStartSearch.value) {
		return
	}

	searchIsLoading.value = true

	try {
		await chatStore.searchChannelMessages(searchString.value, null, null, null)
		showingResults.value = true

		// go to first occurance
		if (chatStore.searchChannelMessagesResultItems.length) {
			goToSearchStep(1)
		}
	} catch (e) {
		clearSearch()
	} finally {
		searchIsLoading.value = false
	}
}

const goToSearchStep = (step: number) => {
	if (step < 1) {
		// underdrew -> go to the end
		step = chatStore.searchChannelMessagesResultItemsFlat.length
	} else if (step > chatStore.searchChannelMessagesResultItemsFlat.length) {
		// overdrew -> go back to the beggining
		step = 1
	}

	const searchItemToScrollTo =
		chatStore.searchChannelMessagesResultItemsFlat[step - 1]

	chatStore.handleScroll(
		true,
		true,
		searchItemToScrollTo?.sessionId,
		searchItemToScrollTo?.messageIndex,
		true
	)

	chatStore.searchChannelMessagesCurrentSearchStep = step
}

/**
 * shortcut to next result by pressing f3 / shift + f3
 * @param e
 */
const searchPrevNextShortcut = (e: KeyboardEvent) => {
	e.preventDefault()

	// increase step
	let gotoStep = chatStore.searchChannelMessagesCurrentSearchStep + 1

	if (e.shiftKey) {
		// decrese step if shift is pressed
		gotoStep = chatStore.searchChannelMessagesCurrentSearchStep - 1
	}

	goToSearchStep(gotoStep)
}

/**
 * back button
 */
const goBack = () => {
	router.back()
}

onUnmounted(() => {
	clearSearch()
})
</script>

<style scoped></style>
